import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { Customer } from 'src/features/customers/utils/types';
import { useDeleteCustomer } from 'src/features/customers/api/useDeleteCustomer';
import { LIST_CUSTOMERS_RESOURCE } from 'src/features/customers/api/useListCustomers';
import { customersMessages } from 'src/features/customers/utils/messages';
import { useOnError } from 'src/utils/errors';

interface DeleteCustomerFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedCustomer: Customer;
    children?: React.ReactNode;
}

function DeleteCustomerFormModal<TData>({ selectedCustomer, children }: DeleteCustomerFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const onError = useOnError();

    const { mutate: deletePayStation } = useDeleteCustomer({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_CUSTOMERS_RESOURCE] });
            setIsModalOpen(false);
            navigate(`/customers`);
            toast({
                title: formatMessage(customersMessages.deleteCustomerSuccess),
                description: formatMessage(customersMessages.deleteCustomerSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...customersMessages.deleteCustomer },
                subTitle: {
                    ...customersMessages.deleteCustomerSubtitle,
                    values: {
                        entity: selectedCustomer.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...customersMessages.confirmDeleteCustomer },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: customersMessages.deleteCustomer,
                callbackFn: () => {
                    return (
                        deletePayStation({
                            id: selectedCustomer.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteCustomerFormModal;
